import { FilterCondition } from 'src/store/modules/application/types';
import { Account } from 'msal';
import { all, call, put, retry, takeEvery, select } from 'redux-saga/effects';
import ACTIONS from '../app.constants';
import {
    TimeSheet,
    TimeSheetActionType,
    TimeSheetCountType,
    TimesheetType,
    TimeSheetCategory,
    ExpenseCategory,
    AddTimeSheetCommentActionType,
} from '../modules/timeSheet/type';
import { getAccessToken } from './authentication.sagas';
import { siteActions, timeSheetActions } from '../actions';
import { Api, API_CONFIGURATIONS } from '../services/services';
import { getLoggedInUser } from '../selectors/authentication.selectors';
import { getTimeSheetType } from '../selectors/timeSheet.selectors';
import { Comment, DownloadTemplateDocumentActionType, Form } from '../modules/application/types';
import defaultSearchForm from '../../views/TimeSheet/defaultFilterForm.json';
import { getTimeSheetSearchFormId } from '../selectors/site.selectors';

function* getTimeSheetList(action: TimeSheetActionType) {
    try {
        yield put(timeSheetActions.getTimeSheetList.pending(action.data));
        const token: string = yield call(getAccessToken, 'timesheets.read');
        if (action?.data?.skip === 0) {
            const timeSheetCount: TimeSheetCountType = {
                totalTimeSheetCount: 0,
            };
            // get total count of events
            const countTotal: number = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getTimeSheetCount,
                token,
                action?.payload?.data,
            );
            timeSheetCount.totalTimeSheetCount = countTotal;

            yield put(timeSheetActions.getTimeSheetListCount.success(timeSheetCount));
        }

        const response: TimeSheet[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getTimeSheetList,
            token,
            action.data,
            action.payload.data,
        );
        yield put(
            timeSheetActions.getTimeSheetList.success(
                response.length > 0 ? response : [],
                action.data.limit,
                action.data.skip,
            ),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.getTimeSheetList.error(error));
    }
}

function* getTimeSheetTypes() {
    try {
        yield put(timeSheetActions.getTimeSheetTypes.pending);
        const token: string = yield call(getAccessToken, 'timesheets.read');
        const response: TimesheetType[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getTimeSheetTypes,
            token,
        );
        let editForm: Form = { id: '', name: '', sections: [] };
        if (response.length && response[0].views?.edit?.formId) {
            const formId = response[0].views?.edit?.formId;
            const formsToken: string = yield call(getAccessToken, 'forms.read');
            editForm = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getForm,
                formsToken,
                formId,
            );
        }
        yield put(timeSheetActions.getTimeSheetTypes.success(response.length > 0 ? response : [], editForm));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.getTimeSheetTypes.error(error));
    }
}

function* getTimeSheetCategories() {
    try {
        yield put(timeSheetActions.getTimeSheetCategories.pending);
        const token: string = yield call(getAccessToken, 'timesheets.read');
        const response: TimeSheetCategory[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getTimeSheetCategories,
            token,
        );
        yield put(timeSheetActions.getTimeSheetCategories.success(response.length > 0 ? response : []));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.getTimeSheetCategories.error(error));
    }
}

function* getTimeSheetExpenseCategories() {
    try {
        yield put(timeSheetActions.getTimeSheetExpenseCategories.pending);
        const token: string = yield call(getAccessToken, 'timesheets.read');
        const response: ExpenseCategory[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getTimeSheetExpenseCategories,
            token,
        );
        yield put(timeSheetActions.getTimeSheetExpenseCategories.success(response.length > 0 ? response : []));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.getTimeSheetExpenseCategories.error(error));
    }
}

function* setTimeSheetForSelectedDate(action: TimeSheetActionType) {
    try {
        const date = action.payload.params?.selectedDate as string;
        yield put(timeSheetActions.openTimeCards.pending);
        const loggedInUser: Account = yield select(getLoggedInUser);
        const timeSheetType: TimesheetType[] = yield select(getTimeSheetType);
        const token: string = yield call(getAccessToken, 'timesheets.write');
        const response: TimeSheet = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.setTimeSheetForSelectedDate,
            token,
            date,
            loggedInUser,
            timeSheetType,
        );
        yield put(timeSheetActions.openTimeCards.success(response));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.openTimeCards.error(error));
    }
}

function* getAppointmentBySelectedDate(action: TimeSheetActionType) {
    try {
        const date = action.payload.params?.selectedDate as string;
        yield put(timeSheetActions.getAppointmentsBySelectedDate.pending);
        const token: string = yield call(getAccessToken, 'events.read');
        const timeSheetUserId = action.payload.params?.timeSheetUserId as string;
        const response: TimeSheetCategory[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getAppointmentsBySelectedDate,
            token,
            date,
            timeSheetUserId,
        );
        yield put(timeSheetActions.getAppointmentsBySelectedDate.success(response.length > 0 ? response : []));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.getAppointmentsBySelectedDate.error(error));
    }
}

function* getTimeSheetCommentList(action: TimeSheetActionType) {
    const timesheetId: string = action.payload.id;
    try {
        yield put(timeSheetActions.getTimeSheetCommentList.pending);
        const token: string = yield call(getAccessToken, 'timesheets.read');
        const response: [] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getTimeSheetCommentsList,
            token,
            timesheetId,
        );
        yield put(timeSheetActions.getTimeSheetCommentList.success(response.length > 0 ? response : []));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.getTimeSheetCommentList.error(error));
    }
}

function* addTimeSheetComment(action: AddTimeSheetCommentActionType) {
    const comment: string = action.payload.comment;
    const timesheetId: string = action.payload.timeSheetId;
    try {
        yield put(timeSheetActions.addTimeSheetComment.pending);
        const token: string = yield call(getAccessToken, 'timesheets.write');
        const result: Comment = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addTimeSheetComment,
            token,
            comment,
            timesheetId,
        );
        yield put(timeSheetActions.addTimeSheetComment.success(result));
        yield put(
            siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Time Sheet comment has been added.' }),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.addTimeSheetComment.error(error));
    }
}

function* getTimeSheetDetail(action: TimeSheetActionType) {
    const timesheetId: string = action.payload.params?.timeSheetId as string;
    try {
        yield put(timeSheetActions.getTimeSheetDetails.pending);
        const token: string = yield call(getAccessToken, 'timesheets.read');
        const result: TimeSheet = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getTimeSheetDetails,
            token,
            timesheetId,
        );

        yield put(timeSheetActions.getTimeSheetDetails.success(result));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.getTimeSheetDetails.error(error));
    }
}

function* saveTimeSheet(action: TimeSheetActionType) {
    const timeSheet: TimeSheet = action.payload.params?.timeSheet as TimeSheet;
    const displayMessage: string = action.payload.params?.displayMessage as string;
    const timeSheetId: string = action.payload.params?.timeSheetId as string;
    const listFilters: FilterCondition[] = action.payload.params?.listFilters as FilterCondition[];
    try {
        yield put(timeSheetActions.saveTimeSheet.pending);
        const token: string = yield call(getAccessToken, 'timesheets.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.saveTimeSheet,
            token,
            timeSheet,
            timeSheetId,
        );

        yield put(timeSheetActions.saveTimeSheet.success(timeSheet));
        yield put(
            timeSheetActions.getTimeSheetListBegin(
                {
                    limit: 20,
                    skip: 0,
                    firstLoad: false,
                    orderCol: '',
                    orderBy: '',
                },
                listFilters,
            ),
        );

        yield put(
            siteActions.setSuccess({
                isError: false,
                errorCode: 0,
                message: displayMessage,
            }),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.saveTimeSheet.error(error));
    }
}

function* mergeTimesheetDocuments(action: DownloadTemplateDocumentActionType) {
    const templateId = action.payload.templateId;
    const fileName = action.payload.fileName;
    const timesheetId = action.payload.timesheetId!;
    try {
        yield put(timeSheetActions.mergeTimesheetDocuments.pending);
        const token: string = yield call(getAccessToken, 'timesheets.read');
        const result = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.mergeTimesheetDocuments,
            token,
            templateId,
            fileName,
            timesheetId,
        );
        yield put(timeSheetActions.mergeTimesheetDocuments.success(result));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: result.response }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(timeSheetActions.mergeTimesheetDocuments.error(error));
    }
}

function* getTimesheetSearchForm() {
    try {
        let searchForm: Form = defaultSearchForm;
        yield put(timeSheetActions.getTimesheetSearchForm.pending);
        const token: string = yield call(getAccessToken, 'forms.read');
        const formId: string = yield select(getTimeSheetSearchFormId);
        if (!!formId) {
            try {
                searchForm = yield retry(
                    API_CONFIGURATIONS.MAX_RETRY_COUNT,
                    API_CONFIGURATIONS.RETRY_INTERVAL,
                    Api.getForm,
                    token,
                    formId,
                );
            } catch (error) {
                yield put(timeSheetActions.getTimesheetSearchForm.error(error));
            }
        }
        yield put(timeSheetActions.getTimesheetSearchForm.success(searchForm));
    } catch (error) {
        yield put(siteActions.setError(error));
    }
}

function* applyFilter(action: TimeSheetActionType) {
    yield getTimeSheetList(action);
}

export default function* rootSaga() {
    yield all([takeEvery(ACTIONS.TIME_SHEET_LIST_BEGIN, getTimeSheetList)]);
    yield all([takeEvery(ACTIONS.GET_TIME_SHEET_TYPES_BEGIN, getTimeSheetTypes)]);
    yield all([takeEvery(ACTIONS.GET_TIME_SHEET_CATEGORIES_BEGIN, getTimeSheetCategories)]);
    yield all([takeEvery(ACTIONS.OPEN_TIME_SHEET_FOR_SELECTED_DATE_BEGIN, setTimeSheetForSelectedDate)]);
    yield all([takeEvery(ACTIONS.GET_APPOINTMENT_BY_SELECTED_DATE_BEGIN, getAppointmentBySelectedDate)]);
    yield all([takeEvery(ACTIONS.GET_TIME_SHEET_EXPENSE_CATEGORIES_BEGIN, getTimeSheetExpenseCategories)]);
    yield all([takeEvery(ACTIONS.GET_TIME_SHEET_COMMENT_LIST_BEGIN, getTimeSheetCommentList)]);
    yield all([takeEvery(ACTIONS.ADD_TIME_SHEET_COMMENT_BEGIN, addTimeSheetComment)]);
    yield all([takeEvery(ACTIONS.GET_TIME_SHEET_DETAILS_BEGIN, getTimeSheetDetail)]);
    yield all([takeEvery(ACTIONS.SAVE_TIME_SHEET_BEGIN, saveTimeSheet)]);
    yield all([takeEvery(ACTIONS.DOWNLOAD_TIMESHEET_PACKET_BEGIN, mergeTimesheetDocuments)]);
    yield all([takeEvery(ACTIONS.GET_TIMESHEET_SEARCH_FORM_BEGIN, getTimesheetSearchForm)]);
    yield all([takeEvery(ACTIONS.APPLY_TIMESHEET_FILTERS, applyFilter)]);
}
